import { Box, Button, ButtonProps, Stack, SxProps, Theme } from "@mui/material";
import { useCountdown } from "hooks";

interface PaymentCtaButtonProps {
  label: string;
  onClick: () => void;
  countdown?: number;
  loading?: boolean;
  color?: "primary" | "error" | "warning" | "success";
}

export const PaymentCtaButton = ({
  label,
  onClick,
  countdown,
  loading,
  color = "primary",
  ...rest
}: PaymentCtaButtonProps & ButtonProps) => {
  const hasCountdown = countdown !== undefined;
  const { remaining } = useCountdown(countdown ?? 0, onClick, hasCountdown);

  return (
    <Button
      variant="contained"
      color={color}
      fullWidth
      onClick={onClick}
      disabled={loading}
      {...rest}
    >
      <Stack direction="row" sx={hasCountdown ? countdownBtnProps : undefined}>
        {hasCountdown ? (
          <>
            {label} <Countdown countdown={countdown} remaining={remaining} />
          </>
        ) : (
          label
        )}
      </Stack>
    </Button>
  );
};

const countdownBtnProps: SxProps<Theme> = {
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  width: "100%",
};

const Countdown = ({
  countdown,
  remaining,
  size = 42,
}: {
  countdown: number;
  remaining: number;
  size?: number;
}) => {
  const progress = Math.max(0, remaining) / (countdown ?? 1);
  const r = 11;
  const C = 2 * Math.PI * r;
  const offset = C * (1 - progress);
  const display = Math.max(0, remaining);

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        my: `-${size / 2}px`,
        mx: `-${size / 4}px`,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 32 32"
        sx={{
          position: "absolute",
          inset: 0,
          transform: "rotate(-90deg)",
          width: "100%",
          height: "100%",
        }}
      >
        <circle
          cx="16"
          cy="16"
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.25)"
          strokeWidth="2"
        />
        <circle
          cx="16"
          cy="16"
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeDasharray={C}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s linear" }}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          fontSize: "0.825rem",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {display}
      </Box>
    </Box>
  );
};
